/**
 * Per-tab storage that survives a reload.
 *
 * Two pieces of softphone state need exactly this lifetime — "this browser tab, across
 * reloads, gone when the tab closes":
 *
 *  - the **session-lock tab id** (`session-lock.ts`), so a reload reclaims its own line instead
 *    of standing in the queue behind the record it wrote itself two seconds ago;
 *  - the **Verto sessid** (`softphone-context.tsx`), which is what lets FreeSWITCH re-attach
 *    live calls to the new page.
 *
 * `localStorage` is wrong for both: it is shared by every tab, which is the exact distinction
 * the lock exists to make, and a sessid leaking to a second tab would have two pages claiming
 * one Verto session. `sessionStorage` is per-tab and reload-surviving, which is the shape both
 * of these actually are.
 *
 * Everything is wrapped: Safari's private mode and "block all cookies" throw on access rather
 * than returning null, and neither of these features is important enough to break the console.
 * The fallbacks are chosen so that losing storage degrades to today's behaviour — a fresh id
 * and no recovery — never to something unsafe.
 *
 * One quirk worth knowing: Chrome and Safari COPY sessionStorage into a duplicated tab. So a
 * duplicate starts life claiming the same tab id, which is why the lock does not treat the tab
 * id alone as proof of ownership — see the `sessionId` there.
 */

export function readTab(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

export function writeTab(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    /* non-fatal — the caller's feature degrades to "no memory across reloads" */
  }
}

export function clearTab(key: string): void {
  try {
    sessionStorage.removeItem(key);
  } catch {
    /* non-fatal */
  }
}

/** A value generated once per tab and kept for as long as the tab lives. */
export function stickyTabValue(key: string, make: () => string): string {
  const existing = readTab(key);
  if (existing) return existing;
  const created = make();
  writeTab(key, created);
  return created;
}

export function randomId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}
